const API_URL = import.meta.env.VITE_API_URL;
const TOKEN_KEY = 'spares.accessToken';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
}

async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${API_URL}${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });

  if (!response.ok) throw await errorFrom(response);

  return (await response.json()) as T;
}

async function errorFrom(response: Response): Promise<ApiError> {
  let message = `Request failed with status ${response.status}`;
  try {
    const data = (await response.json()) as { message?: string | string[] };
    if (data.message) {
      message = Array.isArray(data.message)
        ? data.message.join(', ')
        : data.message;
    }
  } catch {
    // Non-JSON error body; keep the default message.
  }
  return new ApiError(response.status, message);
}

// For file downloads: the JWT travels in a header, so a plain link cannot
// fetch them.
async function requestFile(path: string): Promise<Blob> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_URL}${path}`, { headers });
  if (!response.ok) throw await errorFrom(response);
  return await response.blob();
}

export interface LoginResponse {
  accessToken: string;
}

export interface MeResponse {
  id: number;
  email: string;
}

export interface HealthResponse {
  status: 'ok';
}

export interface TriggerSyncResponse {
  enqueued: boolean;
  jobId: string;
  /** 'requeued' when a finished job was replaced so the retry actually runs. */
  outcome: 'queued' | 'requeued' | 'already-running';
}

export interface SyncChangedResponse {
  enqueued: number;
  /** Of those enqueued, how many replaced a finished job for the product. */
  requeued: number;
  /** Skipped because their job was still queued or running. */
  alreadyQueued: number;
  /** More products were owed a sync than the batch limit allowed. */
  truncated: boolean;
  /** Products held back for having no ai_description yet. */
  skippedWithoutDescription: number;
}

// Who asked for the run: a night, or one of the panel's buttons.
export type SyncRunTrigger = 'nightly' | 'changed' | 'backlog' | 'product';

export interface SyncRunCounters {
  productsCreated: number;
  productsUpdated: number;
  productsFailed: number;
  variantsCreated: number;
  variantsUpdated: number;
  variantsZeroed: number;
  pricesChanged: number;
  stockChanged: number;
}

export interface SyncRun {
  id: string;
  trigger: SyncRunTrigger;
  startedAt: number;
  /** Null while the run is still going. */
  finishedAt: number | null;
  enqueued: number;
  sealed: boolean;
  /** Products that have reported back, of `enqueued`. */
  done: number;
  heldBackWithoutDescription: number;
  counters: SyncRunCounters;
  /** The run's CSV in storage; null while it runs, or if it wrote nothing. */
  reportKey: string | null;
  /** Why the run ended with products still flagged. */
  stoppedReason: string | null;
}

export interface SyncRunsResponse {
  /** Runs across all pages. */
  total: number;
  runs: SyncRun[];
}

// Read live from the store. 'unpublished': listed but off the Online Store, so
// not buyable. 'unknown': the store could not be asked; never shown as a No.
export type ListingReport = 'live' | 'unpublished' | 'absent' | 'unknown';

export interface CatalogueProduct {
  id: number;
  sku: string;
  reference: string;
  brandName: string | null;
  shopifyHandle: string | null;
  listing: ListingReport;
  hasDescription: boolean;
  hasImage: boolean;
  priceRows: number;
  suppliers: number;
  totalStock: number;
  minCost: string | null;
  maxCost: string | null;
  lastChangeAt: number | null;
  weightKg: string | null;
  gtin: string | null;
  hsCode: string | null;
  categoryName: string | null;
}

// One supplier's offer for a product. `salePrice` is computed by the API from
// cost and the supplier's markup, as the sync does.
export interface ProductOffer {
  priceId: number;
  supplierName: string;
  conditionName: string;
  cost: string;
  salePrice: string;
  currency: string;
  // Read live from the store, matching variant SKUs to price ids. Null when
  // the store could not be asked.
  inShopify: boolean | null;
}

export interface ProductOffersResponse {
  // Why the store could not be asked. Null when it answered or there is no
  // listing.
  shopifyError: string | null;
  offers: ProductOffer[];
}

export interface CatalogueResponse {
  products: CatalogueProduct[];
  // Why the store could not be asked. Null when it answered.
  shopifyError: string | null;
  total: number;
  limit: number;
  offset: number;
}

export interface SuppliersResponse {
  suppliers: string[];
  // Subset of `suppliers` whose feed the API fetches; no file to upload.
  pullSuppliers: string[];
  // Subset of `suppliers` with an image feed, synced by a separate pass.
  imageSuppliers: string[];
}

export interface TriggerIngestionResponse {
  enqueued: boolean;
  jobId: string;
  /**
   * `already-running`: a job for that feed or pass was already in flight;
   * `jobId` is that job's.
   */
  outcome: 'queued' | 'already-running';
}

export interface IngestionJobResult {
  rowsProcessed: number;
  rowErrors: number;
  errorReportKey: string | null;
  // The storage error when the error CSV could not be saved; the counts are
  // still exact.
  errorReportLost: string | null;
  // Brands the pass could not place; each needs an alias in brand-map.data.ts, a
  // brand row or blacklisting, then a re-ingest. Null when all were placed.
  unresolvedBrands: {
    names: string[];
    // The list is capped; the error CSV names every rejected row.
    truncated: boolean;
  } | null;
  imagesInserted: number;
  priceImagesLinked: number;
  productsInserted: number;
  pricesInserted: number;
  pricesUpdated: number;
  stockUpdated: number;
  brandsNotFound: number;
  pricesZeroed: number;
  // Stopped by an operator: rows read were saved, the rest never read, so
  // nothing was zeroed.
  cancelled: boolean;
  // A guard tripped: nothing was zeroed, the file's prices were written.
  zeroingRefused: ZeroingRefused | null;
  // Rows skipped by rule, by reason; not errors.
  rowsSkipped: Partial<Record<RowSkipReason, number>>;
}

export type RowSkipReason =
  | "no-reference"
  | "no-condition"
  | "condition-not-sold"
  | "lead-time-not-sold"
  | "no-price"
  | "price-below-minimum";

// Why nothing was zeroed: too much stock absent from the file, or too many
// unreadable rows.
export type ZeroingRefused =
  | { reason: "absent"; wouldZero: number; inStock: number }
  | { reason: "read-errors"; rowErrors: number; rowsProcessed: number };

export function isReadErrorRefusal(
  refused: ZeroingRefused,
): refused is Extract<ZeroingRefused, { reason: "read-errors" }> {
  return refused.reason === "read-errors";
}

export interface ImageSyncJobResult {
  pricesNeedingImages: number;
  itemsTargeted: number;
  itemsFoundInFeed: number;
  imagesDownloaded: number;
  imagesReused: number;
  priceImagesLinked: number;
  productImagesLinked: number;
  skippedGeneric: number;
  skippedOverCap: number;
  stoppedAtLimit: boolean;
  cancelled: boolean;
  imageErrors: number;
  errorReportKey: string | null;
}

export interface DescriptionsJobResult {
  productsNeedingDescription: number;
  generated: number;
  // Generated, then discarded because the row was filled meanwhile.
  alreadyFilled: number;
  descriptionErrors: number;
  // The pass hit DESCRIPTIONS_MAX_PER_RUN with work left; the next one continues.
  stoppedAtLimit: boolean;
  cancelled: boolean;
  errorReportKey: string | null;
}

export type IngestionJobKind = 'stock' | 'images' | 'descriptions';

export type IngestionJobReturnValue =
  | IngestionJobResult
  | ImageSyncJobResult
  | DescriptionsJobResult;

// Result shapes are told apart by their counters: GET /ingestion/jobs/:jobId
// returns no `kind`.
export function isImageResult(
  result: IngestionJobReturnValue,
): result is ImageSyncJobResult {
  return 'imagesDownloaded' in result;
}

export function isDescriptionsResult(
  result: IngestionJobReturnValue,
): result is DescriptionsJobResult {
  return 'productsNeedingDescription' in result;
}

export function isStockResult(
  result: IngestionJobReturnValue,
): result is IngestionJobResult {
  return 'rowsProcessed' in result;
}

export interface IngestionJobSummary {
  id: string;
  supplier: string | null;
  s3Key: string | null;
  fileName: string | null;
  kind: IngestionJobKind;
  state: string;
  attemptsMade: number;
  createdAt: number | null;
  processedAt: number | null;
  finishedAt: number | null;
  progress: unknown;
  returnValue: IngestionJobReturnValue | null;
  failedReason: string | null;
}

export interface IngestionJobsResponse {
  /** Listed jobs across all pages. */
  total: number;
  jobs: IngestionJobSummary[];
}

export interface CancelJobResponse {
  jobId: string;
  state: string;
  // `requested`: a running pass was asked to stop (takes a moment). `removed`:
  // not started, dropped. `already-finished`: ended before the click.
  action: 'requested' | 'removed' | 'already-finished';
}

export interface IngestionJobStatus {
  id: string;
  state: string;
  progress: unknown;
  returnValue: IngestionJobReturnValue | null;
  failedReason: string | null;
}

export const api = {
  login: (email: string, password: string) =>
    request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: { email, password },
    }),
  me: () => request<MeResponse>('/auth/me'),
  health: () => request<HealthResponse>('/health'),
  triggerSync: (productId: number) =>
    request<TriggerSyncResponse>(`/sync/${productId}`, { method: 'POST' }),
  syncChanged: (params: { limit?: number } = {}) => {
    const suffix =
      params.limit === undefined ? '' : `?limit=${String(params.limit)}`;
    return request<SyncChangedResponse>(`/sync/changed${suffix}`, {
      method: 'POST',
    });
  },
  syncRuns: (limit?: number, offset?: number) => {
    const query = new URLSearchParams();
    if (limit !== undefined) query.set('limit', String(limit));
    if (offset !== undefined) query.set('offset', String(offset));
    return request<SyncRunsResponse>(`/sync/runs?${query.toString()}`);
  },
  syncRunReport: (runId: string) =>
    requestFile(`/sync/runs/${encodeURIComponent(runId)}/report`),
  catalogue: (params: {
    search?: string;
    onlyBlocked?: boolean;
    published?: boolean;
    limit?: number;
    offset?: number;
  }) => {
    const query = new URLSearchParams();
    if (params.search) query.set('search', params.search);
    if (params.onlyBlocked) query.set('onlyBlocked', 'true');
    if (params.published !== undefined)
      query.set('published', String(params.published));
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    if (params.offset !== undefined) query.set('offset', String(params.offset));
    const suffix = query.size > 0 ? `?${query}` : '';
    return request<CatalogueResponse>(`/products${suffix}`);
  },
  // Best condition first, and within a condition the supplier the sync would
  // pick, so the offer that reaches Shopify heads its group.
  productOffers: (productId: number) =>
    request<ProductOffersResponse>(`/products/${productId}/offers`),
  suppliers: () => request<SuppliersResponse>('/ingestion/suppliers'),
  // The API fetches the feed itself (`pullSuppliers` only).
  triggerIngestion: (supplier: string) =>
    request<TriggerIngestionResponse>(`/ingestion/${supplier}`, {
      method: 'POST',
    }),
  // `imageSuppliers` only.
  triggerImageSync: (supplier: string) =>
    request<TriggerIngestionResponse>(`/ingestion/${supplier}/images`, {
      method: 'POST',
    }),
  ingestionJobStatus: (jobId: string) =>
    request<IngestionJobStatus>(`/ingestion/jobs/${encodeURIComponent(jobId)}`),
  // Cooperative: the pass finishes the item in hand, saves and exits.
  cancelIngestionJob: (jobId: string) =>
    request<CancelJobResponse>(
      `/ingestion/jobs/${encodeURIComponent(jobId)}/cancel`,
      { method: 'POST' },
    ),
  ingestionJobs: (limit?: number, offset = 0) => {
    const params = new URLSearchParams({ offset: String(offset) });
    if (limit !== undefined) params.set('limit', String(limit));
    return request<IngestionJobsResponse>(`/ingestion/jobs?${params}`);
  },
  // CSV, one line per row the pass could not process.
  ingestionErrorReport: (jobId: string) =>
    requestFile(
      `/ingestion/jobs/${encodeURIComponent(jobId)}/error-report`,
    ),
  novautStatus: () => request<NovautStatus>('/novaut/status'),
  novautRuns: (limit?: number, offset = 0) => {
    const params = new URLSearchParams({ offset: String(offset) });
    if (limit !== undefined) params.set('limit', String(limit));
    return request<NovautRunsResponse>(`/novaut/runs?${params}`);
  },
  // The dry run.
  novautPlan: () =>
    request<TriggerNovautRunResponse>('/novaut/plan', { method: 'POST' }),
  // The file run by hand.
  novautWriteFile: () =>
    request<TriggerNovautRunResponse>('/novaut/files', { method: 'POST' }),
  // The run's log: the listings it held by a problem, with the reason.
  novautRunLog: (jobId: string) =>
    requestFile(`/novaut/runs/${encodeURIComponent(jobId)}/csv`),
  // The exact bytes for the SFTP.
  novautFileCsv: (name: string) =>
    requestFile(`/novaut/files/${encodeURIComponent(name)}/csv`),
};

export type NovautRunKind = 'plan' | 'file';

// Who asked for the run: the 03:00 job, or a button here. A dry run is
// always 'hand'.
export type NovautFileOrigin = 'night' | 'hand';

export interface NovautPlanResult {
  productsWithOffers: number;
  candidates: number;
  rows: number;
  withPictures: number;
  eanLeftOut: number;
  held: Record<string, number>;
  /** Undecided rules the run took a trial value for. */
  tried: string[];
  // The process was shutting down: the rows, the holds and the log cover the
  // products walked until then.
  stoppedByShutdown: boolean;
  // Null when nothing was held and no trial value taken.
  logKey: string | null;
}

export interface NovautFileResult {
  /** Why no file was written although it could be: writes off, a problem in
   * the rules, or a file over 15% smaller than the last one. */
  refused: string | null;
  candidates: number;
  rows: number;
  withPictures: number;
  eanLeftOut: number;
  held: Record<string, number>;
  /** Null when no file was written: refused, no rows, or a restart. */
  fileName: string | null;
  stoppedByShutdown: boolean;
  logKey: string | null;
}

export interface NovautRun {
  id: string;
  kind: NovautRunKind;
  origin: NovautFileOrigin;
  state: string;
  createdAt: number | null;
  processedAt: number | null;
  finishedAt: number | null;
  returnValue: NovautPlanResult | NovautFileResult | null;
  failedReason: string | null;
}

export interface NovautRunsResponse {
  total: number;
  runs: NovautRun[];
}

export interface TriggerNovautRunResponse {
  jobId: string;
  outcome: 'queued' | 'already-running';
}

export interface NovautStatus {
  writesEnabled: boolean;
  /** Why a file run is refused today, or null. */
  fileRunRefusal: string | null;
}

// POSTs the file to the API, which stores it and enqueues ingestion.
// XMLHttpRequest because fetch has no upload-progress events.
export function uploadAndIngest(
  supplier: string,
  file: File,
  onProgress: (percent: number) => void,
): Promise<TriggerIngestionResponse> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}/ingestion/${supplier}/upload`);
    const token = getToken();
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(JSON.parse(xhr.responseText) as TriggerIngestionResponse);
      } else {
        let message = `Upload failed with status ${xhr.status}`;
        try {
          const data = JSON.parse(xhr.responseText) as {
            message?: string | string[];
          };
          if (data.message) {
            message = Array.isArray(data.message)
              ? data.message.join(', ')
              : data.message;
          }
        } catch {
          // Non-JSON error body; keep the default message.
        }
        reject(new ApiError(xhr.status, message));
      }
    };
    xhr.onerror = () => reject(new ApiError(0, 'Upload failed: network error'));
    xhr.send(form);
  });
}
